import type { WarningNotice } from '../statementMeta';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    StatementSkeleton,
    StatementView,
    StatementWarnings,
} from '../components';
import { type StatementColumns, type StatementRow } from '../statementMeta';

type ProfitLossPageViewProps = {
    rows: StatementRow[];
    columns: StatementColumns;
    warningNotices: WarningNotice[];
    /** Postable accounts the layout maps nowhere — the statement is short. */
    unmappedCodes: string[];
    onDismissWarnings: () => void;
    onViewLedger: (accountId: string) => void;
    generatedText: string;
    /**
     * The statement is still in flight. The range control, presets and Print keep
     * their real labels and are disabled; only the statement body skeletons.
     */
    isLoading: boolean;
};

/**
 * Profit & Loss — render only. The shell supplies the page title; this owns the
 * period range (reusing the journal's range control), the quick presets (the
 * journal's segmented pattern), any advisory notices, and the shared statement
 * renderer whose net-result row is the screen's payoff.
 */
export const ProfitLossPageView: React.FC<ProfitLossPageViewProps> = ({
    rows,
    columns,
    warningNotices,
    unmappedCodes,
    onDismissWarnings,
    onViewLedger,
    generatedText,
    isLoading,
}) => {
    const { t } = useTranslation('pages');

    return (
        <section
            aria-label={t('statements.pnl.title')}
            className="flex flex-col gap-4"
        >
            {/* Print is all that is left of the control row — the period
                belongs to the Statements shell above. */}
            <div className="flex flex-wrap items-end justify-end gap-4 print:hidden">
                <Button
                    isDisabled={isLoading}
                    onPress={() => window.print()}
                    variant="outline"
                    {...getNodeTestIdProp('profit-loss-print')}
                >
                    <Icon aria-hidden icon="gravity-ui:printer" />
                    {t('statements.print')}
                </Button>
            </div>

            <StatementWarnings
                notices={warningNotices}
                unmappedCodes={unmappedCodes}
                onDismiss={onDismissWarnings}
                testIdPrefix="profit-loss"
            />

            {isLoading ? (
                <StatementSkeleton testIdPrefix="profit-loss" />
            ) : (
                <StatementView
                    ariaLabel={t('statements.pnl.ariaLabel')}
                    footerNote={t('statements.footer.currencyNote')}
                    generatedText={t('statements.footer.generated', {
                        datetime: generatedText,
                    })}
                    columns={columns}
                    onViewLedger={onViewLedger}
                    rows={rows}
                    testIdPrefix="profit-loss"
                />
            )}
        </section>
    );
};
